import { useMemo, useState } from 'react';
import { Award, Trophy, Shirt, Music, Target, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { competitions } from '../mock';
import SampleEntries from '../components/SampleEntries';

// Competitions that accept online entries via the form.
// Costume Parade allows optional pre-registration; the others require it.
const REGISTERABLE_COMPETITIONS = [
  'Twin Waters Everyday Heroes',
  "Best Dog Dad (Father's Day)",
  'Paws of Purpose Costume Parade',
];

const WORD_SUBMISSION_LABELS = {
  'Twin Waters Everyday Heroes': 'Word Submission — Why is your dog an everyday hero? (approx. 100–250 words)',
  "Best Dog Dad (Father's Day)": 'Word Submission — What makes him a great dog dad? (approx. 100–250 words)',
};
const WORD_SUBMISSION_DEFAULT_LABEL = 'Word Submission';

const WORD_SUBMISSION_PLACEHOLDERS = {
  'Twin Waters Everyday Heroes': "Tell us your dog's story — the small moments, the big rescues, the unconditional love...",
  "Best Dog Dad (Father's Day)": 'Tell us what makes him the ultimate dog dad — early walks, treat-sneaking, late-night cuddles...',
};
const WORD_SUBMISSION_DEFAULT_PLACEHOLDER = "Any extra notes you'd like to share...";

const getWordSubmissionLabel = (competition) =>
  WORD_SUBMISSION_LABELS[competition] || WORD_SUBMISSION_DEFAULT_LABEL;

const getWordSubmissionPlaceholder = (competition) =>
  WORD_SUBMISSION_PLACEHOLDERS[competition] || WORD_SUBMISSION_DEFAULT_PLACEHOLDER;

const Competitions = () => {
  const [activeTab, setActiveTab] = useState('info');
  const [formData, setFormData] = useState({
    ownerName: '',
    email: '',
    phone: '',
    dogName: '',
    breed: '',
    age: '',
    competition: '',
    category: '',
    specialNeeds: '',
    heroStory: '',
    photoFile: null
  });

  // Memoize the small list of competitions eligible for the entry form dropdown.
  // Static input + static filter → recomputes only if `competitions` reference changes.
  const registerableCompetitions = useMemo(
    () => competitions.filter((comp) => REGISTERABLE_COMPETITIONS.includes(comp.name)),
    [],  // `competitions` (from mock.js) and `REGISTERABLE_COMPETITIONS` (module const) are both static — deps intentionally empty
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'competition') {
      setFormData(prev => ({ ...prev, category: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const isKidsColouring = formData.competition === 'Paws of Purpose Kids Colouring Contest';
    const needsPhoto =
      formData.competition === 'Twin Waters Everyday Heroes' ||
      formData.competition === "Best Dog Dad (Father's Day)";
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;

      // Read photo (if provided) to base64 — strip the "data:image/...;base64," prefix
      let photoBase64 = null;
      let photoFilename = null;
      if (needsPhoto && formData.photoFile) {
        photoFilename = formData.photoFile.name;
        photoBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result || '';
            const idx = result.indexOf('base64,');
            resolve(idx >= 0 ? result.slice(idx + 7) : result);
          };
          reader.onerror = reject;
          reader.readAsDataURL(formData.photoFile);
        });
      }

      // Transform formData to match backend schema (camelCase to snake_case)
      const payload = {
        owner_name: formData.ownerName,
        email: formData.email,
        phone: formData.phone,
        dog_name: formData.dogName,
        // For Kids Colouring, breed is N/A — auto-fill so backend validation passes
        breed: isKidsColouring ? 'N/A — Kids Colouring' : formData.breed,
        age: formData.age,
        competition: formData.competition,
        category: formData.category || null,
        special_needs: formData.specialNeeds || null,
        hero_story: formData.heroStory || null,
        photo_filename: photoFilename,
        photo_base64: photoBase64,
      };
      
      const response = await fetch(`${API_URL}/api/competitions/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to submit registration');
      }

      await response.json();
      
      toast.success('Registration submitted successfully!', {
        description: isKidsColouring
          ? `${formData.dogName} has been registered for the Kids Colouring Contest`
          : `${formData.dogName} has been registered for ${formData.competition}`
      });
      
      // Reset form
      setFormData({
        ownerName: '',
        email: '',
        phone: '',
        dogName: '',
        breed: '',
        age: '',
        competition: '',
        category: '',
        specialNeeds: '',
        heroStory: '',
        photoFile: null
      });
      // Also reset the file input element
      const fileInput = document.getElementById('photoFile');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      toast.error('Failed to submit registration', {
        description: 'Please try again later or contact us directly.'
      });
    }
  };

  const competitionIcons = {
    'Twin Waters Everyday Heroes': Award,
    'Best Dog Dad (Father\'s Day)': Trophy,
    'Musical Sits': Music,
    'Red Light Green Light': Target,
    'Paws of Purpose Costume Parade': Shirt,
    'Paws of Purpose Kids Colouring Contest': Award
  };

  const selectedCompetition = competitions.find(c => c.name === formData.competition);
  const isKidsColouring = formData.competition === 'Paws of Purpose Kids Colouring Contest';

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/13042701/pexels-photo-13042701.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Dog competition"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/95 via-cyan-50/90 to-white"></div>
        </div>
        
        <div className="relative container mx-auto px-4 py-16 md:py-20">
          <div className="max-w-5xl mx-auto text-center">
            <Badge className="mb-4 bg-cyan-100 text-cyan-800 hover:bg-cyan-200 border-cyan-200">
              Fun & Prizes
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 tracking-tight">
              Competitions & Activities
            </h1>
            <p className="text-lg text-slate-600 mb-12">
              Choose a competition to learn more and enter. All entries are FREE!
            </p>

            {/* Competition Buttons */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
              {competitions.map((competition) => {
                const Icon = competitionIcons[competition.name] || Award;
                // First competition uses #everyday-hero, Best Dog Dad uses #dog-dad, others use #competition-{id}
                let anchorId;
                if (competition.name === "Twin Waters Everyday Heroes") {
                  anchorId = 'everyday-hero';
                } else if (competition.name === "Best Dog Dad (Father's Day)") {
                  anchorId = 'dog-dad';
                } else {
                  anchorId = `competition-${competition.id}`;
                }
                return (
                  <button
                    key={competition.id}
                    onClick={() => {
                      const element = document.getElementById(anchorId);
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                    data-testid={`learn-more-${anchorId}`}
                    className="group relative bg-white hover:bg-gradient-to-br hover:from-cyan-50 hover:to-teal-50 border-2 border-slate-200 hover:border-cyan-400 rounded-xl p-6 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 text-left"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="bg-gradient-to-br from-cyan-100 to-teal-100 group-hover:from-cyan-600 group-hover:to-teal-600 w-14 h-14 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300">
                        <Icon className="h-7 w-7 text-cyan-700 group-hover:text-white transition-colors duration-300" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-slate-900 text-lg mb-1 group-hover:text-cyan-700 transition-colors">
                          {competition.name}
                        </h3>
                        <p className="text-sm text-slate-600 line-clamp-2">
                          {competition.description}
                        </p>
                        <div className="mt-3 flex items-center text-cyan-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                          Learn More <ArrowRight className="ml-1 h-4 w-4" />
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Sample Entries — inspire real submissions */}
      <SampleEntries />

      {/* Competition Details Sections */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto space-y-16">
            {/* Everyday Heroes - Featured Competition */}
            <div id="everyday-hero" className="scroll-mt-20">
              <Card className="border-2 border-cyan-300 bg-gradient-to-br from-cyan-50 to-white">
                <CardHeader>
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="bg-gradient-to-br from-cyan-600 to-teal-600 w-16 h-16 rounded-lg flex items-center justify-center">
                      <Award className="h-8 w-8 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-2xl md:text-3xl">Twin Waters Everyday Heroes</CardTitle>
                      <Badge className="mt-2 bg-amber-100 text-amber-800 hover:bg-amber-200 border-amber-200">
                        Registration Required
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                    <div className="prose prose-slate max-w-none">
                      <p className="text-lg font-medium text-slate-800 italic">
                        Not all heroes wear capes—some have paws.
                      </p>
                      <p className="text-base text-slate-700 leading-relaxed">
                        Our Everyday Dog Hero Contest celebrates the dogs who make a difference in the quiet, 
                        meaningful ways that matter most. This is your chance to share the story of your dog 
                        and why they are your personal hero.
                      </p>
                      <p className="text-base text-slate-700 leading-relaxed">
                        Whether they've helped you through a difficult time, bring daily comfort and companionship, 
                        protect your family, or simply fill your life with unconditional love and joy—every story matters.
                      </p>
                      <p className="text-base text-slate-700 leading-relaxed">
                        We invite community members to submit a short entry telling us what makes their dog an 
                        everyday hero. A <span className="font-semibold">Story Wall</span> will be featured at the 
                        event sharing all the community hero stories submitted and selected stories will be shared 
                        by the MC during the event.
                      </p>
                      <p className="text-base text-slate-700 italic">
                        Because sometimes, the smallest acts of love leave the biggest pawprints.
                      </p>
                    </div>

                    <div className="bg-white rounded-xl p-6 border-2 border-cyan-200">
                      <h3 className="text-xl font-bold text-slate-900 mb-4">How to Enter</h3>
                      <p className="text-base text-slate-700 mb-4">
                        Submit your dog's story via the form below or email us at{' '}
                        <a 
                          href="mailto:paws@twra.net" 
                          className="text-cyan-700 font-semibold hover:underline"
                        >
                          paws@twra.net
                        </a>
                      </p>
                      <ul className="space-y-2 text-base text-slate-700">
                        <li className="flex items-start">
                          <span className="text-cyan-600 mr-2 mt-1">•</span>
                          <span>Your name and your dog's name</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-cyan-600 mr-2 mt-1">•</span>
                          <span>A short story (approx. 100–250 words) about why your dog is an everyday hero</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-cyan-600 mr-2 mt-1">•</span>
                          <span>A photo of your dog</span>
                        </li>
                      </ul>
                      <p className="text-base text-slate-700 mt-4 font-medium">
                        Winners will be announced at the event with special recognition awarded to some of our 
                        most inspiring canine companions.
                      </p>
                      <p className="text-lg text-cyan-700 font-semibold mt-4 italic">
                        Every hero has a story—and we can't wait to hear yours.
                      </p>
                    </div>

                    <div className="flex justify-center">
                      <Button 
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            competition: 'Twin Waters Everyday Heroes',
                            category: '',
                          }));
                          setActiveTab('register');
                          setTimeout(() => {
                            const formEl = document.getElementById('registration-form');
                            if (formEl) formEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }, 100);
                        }}
                        size="lg"
                        className="bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700 text-lg px-8"
                        data-testid="submit-hero-story-btn"
                      >
                        Submit Your Everyday Hero Story
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Other Competitions - Detailed Sections */}
              {competitions.slice(1).map((competition) => {
                const Icon = competitionIcons[competition.name] || Award;
                const anchorId = competition.name === "Best Dog Dad (Father's Day)" ? "dog-dad" : `competition-${competition.id}`;
                return (
                  <div key={competition.id} id={anchorId} className="scroll-mt-20">
                    <Card className="border-slate-200 hover:border-cyan-300 transition-all duration-300">
                      <CardHeader>
                        <div className="flex items-center space-x-3 mb-4">
                          <div className="bg-gradient-to-br from-cyan-100 to-teal-100 w-14 h-14 rounded-lg flex items-center justify-center">
                            <Icon className="h-7 w-7 text-cyan-700" />
                          </div>
                          <div>
                            <CardTitle className="text-2xl">{competition.name}</CardTitle>
                            {competition.name === "Best Dog Dad (Father's Day)" && (
                              <Badge className="mt-2 bg-amber-100 text-amber-800 border-amber-200">
                                Registration Required
                              </Badge>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="prose prose-slate max-w-none">
                          <p className="text-base text-slate-700 leading-relaxed">
                            {competition.description}
                          </p>
                          
                          {competition.fullDescription && (
                            <p className="text-base text-slate-700 leading-relaxed mt-4">
                              {competition.fullDescription}
                            </p>
                          )}

                          {/* Costume Parade Specific Content */}
                          {competition.name === "Paws of Purpose Costume Parade" && (
                            <>
                              <div className="bg-cyan-50 rounded-lg p-4 border border-cyan-200 my-4">
                                <h4 className="font-semibold text-slate-800 mb-2">What does "Paws of Purpose" mean?</h4>
                                <p className="text-slate-700 mb-3">{competition.whatItMeans}</p>
                                <h5 className="font-semibold text-slate-800 mb-2">Your costume can represent:</h5>
                                <ul className="space-y-1">
                                  {competition.costumeIdeas?.map((idea) => (
                                    <li key={idea} className="flex items-start">
                                      <span className="text-cyan-600 mr-2">•</span>
                                      <span className="text-slate-700">{idea}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              
                              <p className="text-slate-700 font-medium">So get creative, have fun, and let your dog's purpose shine!</p>
                              
                              <div className="bg-white rounded-lg p-4 border border-slate-200 my-4">
                                <h4 className="font-semibold text-slate-800 mb-2">Costumes will be judged on:</h4>
                                <ul className="space-y-1">
                                  {competition.judgingCriteria?.map((criteria) => (
                                    <li key={criteria} className="flex items-start">
                                      <span className="text-cyan-600 mr-2">•</span>
                                      <span className="text-slate-700">{criteria}</span>
                                    </li>
                                  ))}
                                </ul>
                                <p className="text-cyan-700 font-semibold mt-3">{competition.prizes}</p>
                              </div>
                            </>
                          )}

                          {/* Costume Parade — 2025 photo */}
                          {competition.name === "Paws of Purpose Costume Parade" && (
                            <div className="my-6">
                              <figure className="relative rounded-xl overflow-hidden border border-slate-200 shadow-lg max-w-2xl mx-auto">
                                <img
                                  src="https://customer-assets.emergentagent.com/job_comp-central-1/artifacts/3zd3g7el_IMG_2029.jpeg"
                                  alt="Paws in the Park 2025 Costume Parade"
                                  className="w-full h-auto"
                                />
                                <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900/90 to-transparent text-white p-4">
                                  <div className="flex items-center space-x-2">
                                    <Trophy className="h-5 w-5 text-amber-300" />
                                    <p className="text-base font-semibold">Paws in the Park 2025</p>
                                  </div>
                                </figcaption>
                              </figure>
                            </div>
                          )}

                          {/* Best Dog Dad Specific Content */}
                          {competition.name === "Best Dog Dad (Father's Day)" && competition.howToEnter && (
                            <div className="bg-white rounded-lg p-4 border border-cyan-200 my-4">
                              <h4 className="font-semibold text-slate-800 mb-3">How to enter</h4>
                              <p className="text-slate-700 mb-3">{competition.howToEnter}</p>
                              <p className="text-slate-700 font-medium">{competition.prizes}</p>
                            </div>
                          )}

                          {/* Kids Colouring Specific Content */}
                          {competition.name === "Paws of Purpose Kids Colouring Contest" && competition.howToEnter && (
                            <div className="bg-white rounded-lg p-4 border border-cyan-200 my-4">
                              <h4 className="font-semibold text-slate-800 mb-3">How It Works</h4>
                              <p className="text-slate-700 mb-3">{competition.howToEnter}</p>
                              <p className="text-cyan-700 font-semibold">{competition.prizes}</p>
                            </div>
                          )}

                          {/* Kids Colouring — preview sheet */}
                          {competition.name === "Paws of Purpose Kids Colouring Contest" && (
                            <div className="my-6">
                              <figure className="rounded-xl overflow-hidden border border-slate-200 shadow-lg max-w-xl mx-auto bg-white">
                                <img
                                  src="https://customer-assets.emergentagent.com/job_comp-central-1/artifacts/24tcy7k0_ChatGPT%20Image%20May%2017%2C%202026%2C%2003_03_15%20PM.png"
                                  alt="Paws for Purpose TWRA 2026 Colouring-In Competition Sheet"
                                  className="w-full h-auto"
                                />
                                <figcaption className="bg-slate-50 p-3 text-center text-sm text-slate-700 font-medium border-t border-slate-200">
                                  Sneak peek of the 2026 Colouring-In Competition sheet
                                </figcaption>
                              </figure>
                            </div>
                          )}
                        </div>
                        
                        {competition.categories && competition.categories.length > 0 && (
                          <div>
                            <h4 className="font-semibold text-slate-800 mb-2">Categories:</h4>
                            <div className="flex flex-wrap gap-2">
                              {competition.categories.map((cat) => (
                                <Badge key={cat} variant="secondary" className="text-sm">
                                  {cat}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {!competition.fullDescription && !competition.howToEnter && (
                          <div className="bg-cyan-50 rounded-lg p-4 border border-cyan-200">
                            <h4 className="font-semibold text-slate-800 mb-2">Prizes:</h4>
                            <p className="text-slate-700">{competition.prizes}</p>
                          </div>
                        )}

                        {/* Only Best Dog Dad + Costume Parade get a register button in this loop.
                            Musical Sits, Red Light Green Light, and Kids Colouring don't require registration. */}
                        {["Best Dog Dad (Father's Day)", "Paws of Purpose Costume Parade"].includes(competition.name) && (
                          <div className="pt-4">
                            <Button 
                              onClick={() => {
                                // Pre-select the competition and switch to the register tab
                                setFormData((prev) => ({
                                  ...prev,
                                  competition: competition.name,
                                  category: '',
                                }));
                                setActiveTab('register');
                                // Smooth-scroll to the form
                                setTimeout(() => {
                                  const formEl = document.getElementById('registration-form');
                                  if (formEl) formEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }, 100);
                              }}
                              className="bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700"
                              data-testid={`register-btn-${competition.id}`}
                            >
                              {competition.name === "Paws of Purpose Costume Parade"
                                ? "Pre-register for Costume Parade (optional)"
                                : `Register for ${competition.name}`}
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Registration Form Section */}
        <section className="py-16 bg-white" id="registration-form">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-12">
                  <TabsTrigger value="info">Competition Info</TabsTrigger>
                  <TabsTrigger value="register">Register Now</TabsTrigger>
                </TabsList>

                <TabsContent value="info" className="space-y-8">
                  <div className="text-center">
                    <h2 className="text-3xl font-bold text-slate-900 mb-4">
                      Ready to Join the Fun?
                    </h2>
                    <p className="text-lg text-slate-600">
                      Scroll up to learn about each competition, or use the register tab to enter now!
                    </p>
                  </div>
                </TabsContent>

              {/* Register Tab */}
              <TabsContent value="register">
                <div className="max-w-2xl mx-auto">
                  <Card className="border-slate-200">
                    <CardHeader>
                      <CardTitle className="text-2xl">Competition Entry Form</CardTitle>
                      <CardDescription>
                        Fill out the form below to register your dog for competitions. 
                        Entry fees will be collected at the event.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Owner / Parent Information */}
                        <div className="space-y-4">
                          <h3 className="font-semibold text-lg text-slate-800 border-b pb-2">
                            {isKidsColouring ? 'Parent / Guardian Information' : 'Owner Information'}
                          </h3>
                          
                          <div className="space-y-2">
                            <Label htmlFor="ownerName">Full Name *</Label>
                            <Input
                              id="ownerName"
                              name="ownerName"
                              value={formData.ownerName}
                              onChange={handleInputChange}
                              required
                              placeholder={isKidsColouring ? "Parent / Guardian name" : "John Smith"}
                            />
                          </div>

                          <div className="grid md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="email">Email *</Label>
                              <Input
                                id="email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                required
                                placeholder={isKidsColouring ? "parent@example.com" : "john@example.com"}
                              />
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor="phone">{isKidsColouring ? 'Parent / Guardian Phone *' : 'Phone Number *'}</Label>
                              <Input
                                id="phone"
                                name="phone"
                                type="tel"
                                value={formData.phone}
                                onChange={handleInputChange}
                                required
                                placeholder="0400 000 000"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Dog / Child Information */}
                        <div className="space-y-4">
                          <h3 className="font-semibold text-lg text-slate-800 border-b pb-2">
                            {isKidsColouring ? 'Child Information' : 'Dog Information'}
                          </h3>
                          
                          <div className="grid md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="dogName">{isKidsColouring ? "Child's Name *" : "Dog's Name *"}</Label>
                              <Input
                                id="dogName"
                                name="dogName"
                                value={formData.dogName}
                                onChange={handleInputChange}
                                required
                                placeholder={isKidsColouring ? "Emma" : "Max"}
                              />
                            </div>

                            {!isKidsColouring && (
                              <div className="space-y-2">
                                <Label htmlFor="breed">Breed *</Label>
                                <Input
                                  id="breed"
                                  name="breed"
                                  value={formData.breed}
                                  onChange={handleInputChange}
                                  required
                                  placeholder="Golden Retriever"
                                />
                              </div>
                            )}
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="age">{isKidsColouring ? "Child's Age *" : 'Age *'}</Label>
                            <Input
                              id="age"
                              name="age"
                              value={formData.age}
                              onChange={handleInputChange}
                              required
                              placeholder={isKidsColouring ? "7" : "3 years"}
                            />
                          </div>
                        </div>

                        {/* Competition Selection */}
                        <div className="space-y-4">
                          <h3 className="font-semibold text-lg text-slate-800 border-b pb-2">
                            Competition Details
                          </h3>
                          
                          <div className="space-y-2">
                            <Label htmlFor="competition">Select Competition *</Label>
                            <Select 
                              value={formData.competition} 
                              onValueChange={(value) => handleSelectChange('competition', value)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Choose a competition" />
                              </SelectTrigger>
                              <SelectContent>
                                {registerableCompetitions.map((comp) => (
                                  <SelectItem key={comp.id} value={comp.name}>
                                    {comp.name}
                                    {comp.name === "Paws of Purpose Costume Parade" && " (optional pre-registration)"}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {formData.competition === "Paws of Purpose Costume Parade" && (
                              <p className="text-xs text-slate-500 mt-1">
                                Pre-registration helps us gauge numbers — but you can also just turn up on the day!
                              </p>
                            )}
                          </div>

                          {selectedCompetition && (
                            <div className="p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                              <div className="flex items-start space-x-2 text-sm">
                                <Award className="h-4 w-4 text-cyan-700 mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className="font-semibold text-slate-800">All competitions are free to enter</p>
                                  <p className="text-slate-600 text-xs mt-1">Just register and join the fun on the day.</p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Photo upload — required for Everyday Heroes and Best Dog Dad */}
                          {(formData.competition === "Twin Waters Everyday Heroes" ||
                            formData.competition === "Best Dog Dad (Father's Day)") && (
                            <div className="space-y-2">
                              <Label htmlFor="photoFile">
                                {formData.competition === "Best Dog Dad (Father's Day)"
                                  ? "Photo of dog dad with their pup *"
                                  : "Photo of your dog *"}
                              </Label>
                              <Input
                                id="photoFile"
                                name="photoFile"
                                type="file"
                                accept="image/*"
                                required
                                onChange={(e) => {
                                  const file = e.target.files?.[0] || null;
                                  if (file && file.size > 5 * 1024 * 1024) {
                                    toast.error('Photo too large', {
                                      description: 'Please choose an image under 5 MB.',
                                    });
                                    e.target.value = '';
                                    return;
                                  }
                                  setFormData((prev) => ({ ...prev, photoFile: file }));
                                }}
                                data-testid="photo-upload-input"
                                className="cursor-pointer"
                              />
                              <p className="text-xs text-slate-500">
                                JPG or PNG, max 5 MB. We'll share standout photos at the event.
                              </p>
                            </div>
                          )}

                          <div className="space-y-2">
                            <Label htmlFor="specialNeeds">
                              {getWordSubmissionLabel(formData.competition)}
                            </Label>
                            <Textarea
                              id="specialNeeds"
                              name="specialNeeds"
                              value={formData.specialNeeds}
                              onChange={handleInputChange}
                              placeholder={getWordSubmissionPlaceholder(formData.competition)}
                              rows={5}
                            />
                          </div>
                        </div>

                        <Button 
                          type="submit" 
                          className="w-full bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700"
                          size="lg"
                        >
                          Submit Registration
                        </Button>

                        <p className="text-xs text-slate-500 text-center">
                          {isKidsColouring
                            ? "Parents/guardians: by submitting this form, you consent to your child's participation in the Paws of Purpose Kids Colouring Contest."
                            : "By submitting this form, you confirm that your dog is up to date with vaccinations and is friendly and socialized with other dogs and people."}
                        </p>
                      </form>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Competitions;
